import { useEffect, useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import api from "@/lib/api";
import { Users, Briefcase, Inbox, TrendingUp, DollarSign, Activity } from "lucide-react";
import { Link } from "react-router-dom";

const fmt = (n) => new Intl.NumberFormat("en-US").format(n);
const money = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const KPI = ({ icon: Icon, label, value, sub, testId, primary }) => (
  <div className={primary ? "fx-kpi-primary" : "ft-card"} data-testid={testId}>
    <div className="flex items-center justify-between">
      <div className="ft-stat-label">{label}</div>
      <Icon size={18} className={primary ? "text-[var(--ft-gold)]" : "text-[var(--ft-muted)]"} strokeWidth={1.6} />
    </div>
    <div className="ft-stat-value mt-3">{value}</div>
    {sub && <div className="text-xs text-[var(--ft-muted)] mt-1">{sub}</div>}
  </div>
);

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const k = data?.kpis;

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="ft-eyebrow mb-2">Executive Dashboard</div>
        <h1 className="text-3xl font-bold tracking-tight">Company overview.</h1>
        <p className="text-[var(--ft-muted)] mt-2">Real-time view of FLOWTALIS operations, pipeline, and revenue.</p>
      </div>

      {data && (
        <>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KPI primary testId="kpi-leads" icon={Inbox} label="Total Leads" value={fmt(k.total_leads)} sub="All-time consultation requests" />
            <KPI primary testId="kpi-clients" icon={Users} label="Active Clients" value={fmt(k.total_clients)} sub="Registered client accounts" />
            <KPI testId="kpi-projects" icon={Briefcase} label="Active Projects" value={fmt(k.active_projects)} sub={`${fmt(k.total_projects)} total launched`} />
            <KPI testId="kpi-reorder" icon={Activity} label="Reorder Rate" value={`${Math.round(k.reorder_rate * 100)}%`} sub="Rolling 12-month" />
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <KPI testId="kpi-pipeline" icon={TrendingUp} label="Pipeline Value" value={money(k.estimated_pipeline_value)} sub="Estimated open pipeline" />
            <KPI testId="kpi-monthly" icon={DollarSign} label="Monthly Revenue" value={money(k.estimated_monthly_revenue)} sub="Est. from active projects" />
            <KPI testId="kpi-avg-completion" icon={Activity} label="Avg Completion" value={`${k.avg_project_completion_weeks} wk`} sub="Proposal → Delivered" />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="ft-card">
              <h2 className="text-lg font-semibold mb-4">Pipeline by Stage</h2>
              <div className="space-y-3">
                {Object.entries(data.pipeline_by_stage).map(([stage, count]) => {
                  const max = Math.max(...Object.values(data.pipeline_by_stage), 1);
                  return (
                    <div key={stage}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium">{stage}</span>
                        <span className="text-[var(--ft-muted)]">{count}</span>
                      </div>
                      <div className="h-1.5 bg-[var(--ft-border)] rounded-full overflow-hidden">
                        <div className="h-full transition-all" style={{ width: `${(count / max) * 100}%`, background: "var(--ft-blue)" }} />
                      </div>
                    </div>
                  );
                })}
                {Object.keys(data.pipeline_by_stage).length === 0 && (
                  <div className="text-sm text-[var(--ft-muted)]">No projects in pipeline yet.</div>
                )}
              </div>
            </div>

            <div className="ft-card">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Recent Leads</h2>
                <Link to="/admin/leads" className="ft-btn-ghost text-sm">View all →</Link>
              </div>
              <div className="space-y-3">
                {data.recent_leads.length === 0 && <div className="text-sm text-[var(--ft-muted)]">No leads yet.</div>}
                {data.recent_leads.map((l) => (
                  <div key={l.id} className="flex justify-between items-start py-2 border-b border-[var(--ft-border)] last:border-b-0">
                    <div>
                      <div className="font-semibold text-sm">{l.full_name}</div>
                      <div className="text-xs text-[var(--ft-muted)]">{l.product_interest}</div>
                    </div>
                    <span className="ft-badge">{l.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
};

export default AdminDashboard;
